import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { ApiError } from '../lib/api';
import { ErrorBanner } from '../components/ui/ErrorBanner';
import { PageHeader } from '../components/ui/PageHeader';
import { requireAccess } from '../lib/guards';
import { formatMinuten, zeiterfassungApi } from '../lib/zeiterfassung-api';

export const Route = createFileRoute('/zeiterfassung/monat')({
  beforeLoad: () => requireAccess('/api/v1/zeiterfassung/status'),
  component: MonatPage,
});

const MONATSNAMEN = [
  'Januar',
  'Februar',
  'März',
  'April',
  'Mai',
  'Juni',
  'Juli',
  'August',
  'September',
  'Oktober',
  'November',
  'Dezember',
];

const WOCHENTAGE = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];

function MonatPage(): JSX.Element {
  const heute = new Date();
  const [jahr, setJahr] = useState(heute.getFullYear());
  const [monat, setMonat] = useState(heute.getMonth() + 1);

  const monatQ = useQuery({
    queryKey: ['zeiterfassung', 'monat', jahr, monat],
    queryFn: () => zeiterfassungApi.getMonat(jahr, monat),
  });

  const urlaubQ = useQuery({
    queryKey: ['zeiterfassung', 'urlaub', jahr],
    queryFn: () => zeiterfassungApi.getUrlaubsSaldo(jahr),
  });

  function navigate(delta: number): void {
    let m = monat + delta;
    let j = jahr;
    while (m < 1) {
      m += 12;
      j -= 1;
    }
    while (m > 12) {
      m -= 12;
      j += 1;
    }
    setJahr(j);
    setMonat(m);
  }

  const summary = monatQ.data?.summary;
  const kumuliert = monatQ.data?.kumulierteUeberstundenMinuten ?? 0;
  const urlaub = urlaubQ.data?.item;

  return (
    <div className="space-y-4 max-w-5xl">
      <PageHeader
        title="Monatsübersicht"
        description="Tagesliste, Saldo, Urlaub."
        actions={
          <a
            href={`/api/v1/zeiterfassung/monat/${jahr}/${monat}/pdf`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            ⬇ Stundenzettel-PDF
          </a>
        }
      />

      <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="rounded-md border border-slate-200 px-3 py-1 text-sm text-slate-700 hover:bg-slate-50"
        >
          ← Vormonat
        </button>
        <div className="text-base font-semibold text-slate-800">
          {MONATSNAMEN[monat - 1]} {jahr}
        </div>
        <button
          type="button"
          onClick={() => navigate(1)}
          className="rounded-md border border-slate-200 px-3 py-1 text-sm text-slate-700 hover:bg-slate-50"
        >
          Folgemonat →
        </button>
      </div>

      {monatQ.isError ? <ErrorBanner error={monatQ.error as ApiError} /> : null}
      {urlaubQ.isError ? <ErrorBanner error={urlaubQ.error as ApiError} /> : null}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {summary ? (
          <div className="lg:col-span-2">
            <SaldoKarte summary={summary} kumuliert={kumuliert} />
          </div>
        ) : (
          <div className="lg:col-span-2 rounded-lg border border-slate-200 bg-white p-6 text-center text-slate-500">
            lädt …
          </div>
        )}
        <div>{urlaub ? <UrlaubKarte saldo={urlaub} jahr={jahr} /> : null}</div>
      </div>

      {summary ? <Tagesliste summary={summary} /> : null}
    </div>
  );
}

function SaldoKarte({
  summary,
  kumuliert,
}: {
  summary: { totalNettoMinuten: number; totalSollMinuten: number; totalDiffMinuten: number };
  kumuliert: number;
}): JSX.Element {
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
      <Stat label="Netto" value={`${formatMinuten(summary.totalNettoMinuten)} h`} />
      <Stat label="Soll" value={`${formatMinuten(summary.totalSollMinuten)} h`} />
      <Stat
        label="Diff Monat"
        value={`${summary.totalDiffMinuten >= 0 ? '+' : ''}${formatMinuten(summary.totalDiffMinuten)} h`}
        accent={summary.totalDiffMinuten >= 0 ? 'text-emerald-700' : 'text-red-700'}
      />
      <Stat
        label="Saldo kumuliert"
        value={`${kumuliert >= 0 ? '+' : ''}${formatMinuten(kumuliert)} h`}
        accent={kumuliert >= 0 ? 'text-emerald-700' : 'text-red-700'}
      />
    </div>
  );
}

function Stat({
  label,
  value,
  accent,
}: {
  label: string;
  value: string;
  accent?: string;
}): JSX.Element {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`text-xl font-semibold ${accent ?? 'text-slate-900'}`}>{value}</div>
    </div>
  );
}

function UrlaubKarte({
  saldo,
  jahr,
}: {
  saldo: {
    anspruchTage: number;
    uebertragTage: number;
    uebertragVerfallen: boolean;
    genommenTage: number;
    restTage: number;
  };
  jahr: number;
}): JSX.Element {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
      <h3 className="text-sm font-semibold text-slate-700">Urlaub {jahr}</h3>
      <dl className="mt-2 space-y-1 text-sm">
        <Row label="Anspruch" value={saldo.anspruchTage} />
        {saldo.uebertragVerfallen ? (
          <Row label="Übertrag" value={saldo.uebertragTage} hint="(verfallen)" />
        ) : (
          <Row label="Übertrag" value={saldo.uebertragTage} />
        )}
        <Row label="Genommen" value={saldo.genommenTage} />
        <hr className="my-2 border-slate-200" />
        <Row
          label="Rest"
          value={saldo.restTage}
          accent={saldo.restTage >= 0 ? 'text-emerald-700' : 'text-red-700'}
          big
        />
      </dl>
    </div>
  );
}

function Row({
  label,
  value,
  hint,
  accent,
  big,
}: {
  label: string;
  value: number;
  hint?: string;
  accent?: string;
  big?: boolean;
}): JSX.Element {
  return (
    <div className="flex items-baseline justify-between">
      <dt className="text-slate-600">
        {label}
        {hint ? <span className="ml-1 text-xs text-slate-400">{hint}</span> : null}
      </dt>
      <dd
        className={`${big ? 'text-lg font-semibold' : 'font-medium'} ${accent ?? 'text-slate-800'}`}
      >
        {value} Tg
      </dd>
    </div>
  );
}

function Tagesliste({
  summary,
}: {
  summary: {
    tage: Array<{
      datum: string;
      bruttoMinuten: number;
      effektivePauseMinuten: number;
      nettoMinuten: number;
      sollMinuten: number;
      diffMinuten: number;
      istFeiertag: boolean;
      feiertagName: string | null;
      abwesenheitTyp: string | null;
      istOffen: boolean;
    }>;
  };
}): JSX.Element {
  return (
    <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
      <table className="min-w-full divide-y divide-slate-200">
        <thead className="bg-slate-50 text-xs font-medium uppercase tracking-wide text-slate-600">
          <tr>
            <th className="px-3 py-2 text-left">Tag</th>
            <th className="px-3 py-2 text-left">Hinweis</th>
            <th className="px-3 py-2 text-right">Brutto</th>
            <th className="px-3 py-2 text-right">Pause</th>
            <th className="px-3 py-2 text-right">Netto</th>
            <th className="px-3 py-2 text-right">Soll</th>
            <th className="px-3 py-2 text-right">Diff</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100 text-sm">
          {summary.tage.map((t) => (
            <tr
              key={t.datum}
              className={`${
                t.istFeiertag || t.abwesenheitTyp ? 'bg-slate-50' : 'hover:bg-slate-50'
              }`}
            >
              <td className="px-3 py-2 font-mono text-xs text-slate-700">
                {WOCHENTAGE[new Date(`${t.datum}T00:00:00Z`).getUTCDay()]} {t.datum.slice(8)}.
              </td>
              <td className="px-3 py-2 text-xs text-slate-600">
                {t.istFeiertag ? `🟢 ${t.feiertagName ?? 'Feiertag'}` : null}
                {t.abwesenheitTyp ? `🛌 ${t.abwesenheitTyp}` : null}
                {t.istOffen ? <span className="ml-1 text-amber-600">⚠ offen</span> : null}
              </td>
              <td className="px-3 py-2 text-right font-mono text-xs text-slate-700">
                {t.bruttoMinuten ? formatMinuten(t.bruttoMinuten) : '–'}
              </td>
              <td className="px-3 py-2 text-right font-mono text-xs text-slate-700">
                {t.effektivePauseMinuten ? formatMinuten(t.effektivePauseMinuten) : '–'}
              </td>
              <td className="px-3 py-2 text-right font-mono text-xs font-semibold text-slate-800">
                {t.nettoMinuten ? formatMinuten(t.nettoMinuten) : '–'}
              </td>
              <td className="px-3 py-2 text-right font-mono text-xs text-slate-600">
                {t.sollMinuten ? formatMinuten(t.sollMinuten) : '–'}
              </td>
              <td
                className={`px-3 py-2 text-right font-mono text-xs ${
                  t.diffMinuten > 0
                    ? 'text-emerald-700'
                    : t.diffMinuten < 0
                      ? 'text-red-700'
                      : 'text-slate-500'
                }`}
              >
                {t.diffMinuten === 0
                  ? '0:00'
                  : `${t.diffMinuten > 0 ? '+' : ''}${formatMinuten(t.diffMinuten)}`}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
